import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Heart, Briefcase, ShoppingBag, Stethoscope, GraduationCap, GameController, Storefront, ArrowRight, CaretLeft, CaretRight, Sparkle, Package, MagnifyingGlass, HandHeart } from "@phosphor-icons/react";
import StarRating from "@/components/StarRating";

const HERO_IMG = "https://images.unsplash.com/photo-1630068846062-3ffe78aa5049?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHx3YXJtJTIwZGl2ZXJzZSUyMGNvbW11bml0eSUyMGdhdGhlcmluZ3xlbnwwfHx8fDE3ODU1Nzc4ODF8MA&ixlib=rb-4.1.0&q=85";
const MATRIMONY_IMG = "https://images.pexels.com/photos/19594072/pexels-photo-19594072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DOCTOR_IMG = "https://images.pexels.com/photos/32115957/pexels-photo-32115957.jpeg";

const services = [
  { to: "/matrimony", label: "Matrimony", desc: "Find your life partner within community & gotra.", icon: Heart },
  { to: "/jobs", label: "Jobs", desc: "Post openings or find work matched to your experience.", icon: Briefcase },
  { to: "/marketplace", label: "Marketplace", desc: "Buy & sell trusted second-hand goods.", icon: ShoppingBag },
  { to: "/training", label: "Training", desc: "Free bite-sized courses across 8 skill tracks.", icon: GraduationCap },
  { to: "/games", label: "Play & Earn", desc: "Play the daily quiz, earn credits for the marketplace.", icon: GameController },
  { to: "/doctor", label: "Doctor Chat", desc: "24×7 AI health guidance & report help.", icon: Stethoscope },
  { to: "/doctors", label: "Doctors", desc: "Ask community doctors, book a clinic visit.", icon: Stethoscope },
  { to: "/support", label: "Support", desc: "Community-funded medical & financial aid.", icon: HandHeart },
];

export default function Landing() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10 fade-up">
            <div className="samaaj-chip mb-6" data-testid="hero-chip">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D95A41]" /> Anveshan Society — over 12,000 families
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tighter text-[#1C1B1A]">
              Every service your <em className="text-[#D95A41] not-italic">samaj</em> needs,<br />
              in one warm place.
            </h1>
            <p className="mt-6 text-lg text-[#5C564D] max-w-xl leading-relaxed">
              Anveshan Society brings matrimony, jobs, marketplace, free training, community games and a doctor at your side — built for the people you already trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register" className="samaaj-btn-primary inline-flex items-center gap-2">
                Join the community <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/training" data-testid="hero-cta-training" className="samaaj-btn-outline">Free training programs</Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-lg">
              <img src={HERO_IMG} alt="Community" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(28,27,26,0.55) 100%)" }} />
              <div className="absolute bottom-6 left-6 right-6 text-[#F9F6F0]">
                <div className="text-xs tracking-widest uppercase opacity-80">Built together</div>
                <div className="font-heading text-2xl leading-tight">Every service you need, from neighbours you already trust.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="services-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">Six services</div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-[#1C1B1A] mt-2">One community, many needs.</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          {/* Matrimony — tall */}
          <Link to="/matrimony" data-testid="tile-matrimony" className="samaaj-card md:col-span-3 md:row-span-2 relative overflow-hidden group">
            <div className="aspect-[4/5] w-full">
              <img src={MATRIMONY_IMG} alt="Matrimony" className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(28,27,26,0.75) 100%)" }} />
            <div className="absolute bottom-0 left-0 right-0 p-8 text-[#F9F6F0]">
              <Heart size={28} weight="fill" />
              <div className="font-heading text-3xl mt-3">Matrimony</div>
              <div className="text-sm mt-2 opacity-90">Filter by community, gotra & profession. AI compatibility on every profile.</div>
            </div>
          </Link>

          {/* Jobs */}
          <Link to="/jobs" data-testid="tile-jobs" className="samaaj-card md:col-span-3 p-8 relative">
            <Briefcase size={28} weight="duotone" color="#2C4A3B" />
            <div className="font-heading text-3xl mt-4 text-[#1C1B1A]">Jobs — post or find</div>
            <div className="text-sm text-[#5C564D] mt-2">Employers post openings. Job-seekers get AI-suggested training when qualifications don't match.</div>
            <div className="mt-4 samaaj-chip">Explore openings <ArrowRight size={14} /></div>
          </Link>

          {/* Training */}
          <Link to="/training" data-testid="tile-training" className="samaaj-card md:col-span-3 p-8 relative" style={{ background: "#EFE4D3" }}>
            <GraduationCap size={28} weight="duotone" color="#D95A41" />
            <div className="font-heading text-3xl mt-4 text-[#1C1B1A]">Free training programs</div>
            <div className="text-sm text-[#5C564D] mt-2">Computer, Electrical, Electronics, Solar, Health, Food, Dress Making — bite-sized weekly lessons.</div>
            <div className="mt-4 samaaj-chip" style={{ background: "#F9F6F0" }}>See tracks <ArrowRight size={14} /></div>
          </Link>

          {/* Marketplace */}
          <Link to="/marketplace" data-testid="tile-marketplace" className="samaaj-card md:col-span-3 p-8 relative">
            <ShoppingBag size={28} weight="duotone" color="#D49A36" />
            <div className="font-heading text-3xl mt-4 text-[#1C1B1A]">Trusted marketplace</div>
            <div className="text-sm text-[#5C564D] mt-2">Buy and sell used electronics, furniture, vehicles — safely paid via Stripe.</div>
            <div className="mt-4 samaaj-chip">Browse listings <ArrowRight size={14} /></div>
          </Link>

          {/* Play & Earn */}
          <Link to="/games" data-testid="tile-games" className="samaaj-card md:col-span-3 p-8 relative" style={{ background: "#F2EBE5" }}>
            <GameController size={28} weight="duotone" color="#2C4A3B" />
            <div className="font-heading text-3xl mt-4 text-[#1C1B1A]">Play & Earn</div>
            <div className="text-sm text-[#5C564D] mt-2">Daily quizzes and community games. Every correct answer adds credits you can spend in the marketplace.</div>
            <div className="mt-4 samaaj-chip" style={{ background: "#F9F6F0" }}>Play a round <ArrowRight size={14} /></div>
          </Link>

          {/* Doctor — wide */}
          <Link to="/doctor" data-testid="tile-doctor" className="samaaj-card md:col-span-6 relative overflow-hidden grid md:grid-cols-2">
            <div className="aspect-[16/9] md:aspect-auto">
              <img src={DOCTOR_IMG} alt="Doctor" className="w-full h-full object-cover" />
            </div>
            <div className="p-8">
              <Stethoscope size={28} weight="duotone" color="#2C4A3B" />
              <div className="font-heading text-3xl mt-4 text-[#1C1B1A]">A doctor by your side, any hour</div>
              <div className="text-sm text-[#5C564D] mt-2 leading-relaxed">Describe symptoms, paste report notes, and get instant, plain-language health guidance from Dr. Anveshan.</div>
              <div className="mt-5 samaaj-chip">Start a chat <ArrowRight size={14} /></div>
            </div>
          </Link>
        </div>
      </section>

      {/* Community Stores carousel */}
      <CommunityStores />
    </div>
  );
}

function CommunityStores() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    const params = q.trim() ? { limit: 12, q: q.trim() } : { limit: 12 };
    const timer = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get("/storefronts", { params }); setStores(data); }
      catch { setStores([]); }
      finally { setLoading(false); }
    }, q ? 220 : 0);
    return () => clearTimeout(timer);
  }, [q]);

  const scrollBy = (dir) => {
    const el = scrollRef.current; if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 720), behavior: "smooth" });
  };

  // Show section always if user searched (so they see "no results"), else only when there are stores
  if (loading && stores.length === 0 && !q) return null;
  if (!q && stores.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="community-stores">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D49A36]">Community stores</div>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-[#1C1B1A] mt-2">Meet the people <em className="text-[#D49A36] not-italic">selling</em>.</h2>
          <p className="text-sm text-[#5C564D] mt-2 max-w-xl">Home-cooks, tailors, refurbishers and community traders — each with their own storefront.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <input
              data-testid="stores-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search stores...`}
              className="pl-8 pr-3 py-2 rounded-full border border-[#E5D9CC] bg-[#F9F6F0] text-sm focus:outline-none focus:border-[#D49A36] w-56"
            />
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C564D]" />
          </div>
          <button data-testid="stores-prev" onClick={() => scrollBy(-1)} className="w-10 h-10 rounded-full flex items-center justify-center border border-[#E5D9CC] bg-[#F2EBE5] hover:bg-[#EFE4D3]" style={{ transition: "background-color 180ms ease-out" }} aria-label="Scroll left">
            <CaretLeft size={16} weight="bold" className="text-[#2C4A3B]" />
          </button>
          <button data-testid="stores-next" onClick={() => scrollBy(1)} className="w-10 h-10 rounded-full flex items-center justify-center border border-[#E5D9CC] bg-[#F2EBE5] hover:bg-[#EFE4D3]" style={{ transition: "background-color 180ms ease-out" }} aria-label="Scroll right">
            <CaretRight size={16} weight="bold" className="text-[#2C4A3B]" />
          </button>
        </div>
      </div>

      {stores.length === 0 ? (
        <div className="samaaj-card p-8 text-center text-sm text-[#5C564D]">No stores match "{q}". Try a different name.</div>
      ) : (
        <div ref={scrollRef} className="overflow-x-auto flex gap-5 pb-2 snap-x snap-mandatory" style={{ scrollbarWidth: "thin" }}>
          {stores.map((s) => (
            <Link key={s.seller_id} to={`/store/${s.seller_id}`} data-testid={`store-card-${s.seller_id}`} className="samaaj-card shrink-0 w-72 overflow-hidden snap-start">
              <div className="aspect-[4/3] relative bg-[#F2EBE5]">
                {s.cover ? (<img src={s.cover} alt={s.storefront_name} className="w-full h-full object-cover" />) : (<div className="w-full h-full flex items-center justify-center"><Storefront size={40} weight="duotone" className="text-[#D95A41]" /></div>)}
                <div className="absolute inset-x-0 bottom-0 h-1/2" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(28,27,26,0.7) 100%)" }} />
                <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-[#F9F6F0] shrink-0 bg-[#D95A41] flex items-center justify-center">
                    {s.storefront_photo ? (<img src={s.storefront_photo} alt="" className="w-full h-full object-cover" />) : (<Storefront size={16} color="#F9F6F0" weight="fill" />)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[#F9F6F0] text-sm font-bold truncate">{s.storefront_name}</div>
                    <div className="text-[#F9F6F0]/85 text-[10px] truncate">by {s.name}</div>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <StarRating value={s.avg_rating || 0} count={s.review_count} showLabel size={12} />
                </div>
                <p className="text-xs text-[#5C564D] line-clamp-2 min-h-[32px] mt-2">{s.storefront_bio || `${s.total} listing${s.total === 1 ? "" : "s"} for the community.`}</p>
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-[#F9F6F0]" style={{ background: "#2C4A3B" }}>{s.total} listing{s.total === 1 ? "" : "s"}</span>
                  {s.fresh > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1" style={{ background: "#DDEBDF", color: "#2C4A3B" }}><Sparkle size={9} weight="fill" /> {s.fresh} fresh</span>}
                  {s.second_hand > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1" style={{ background: "#EFE4D3", color: "#5C564D" }}><Package size={9} /> {s.second_hand} used</span>}
                </div>
                <div className="mt-3 text-xs font-bold text-[#D95A41] flex items-center gap-1">Visit store <ArrowRight size={12} /></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
